import { create } from 'zustand';
import type { PosCheckoutCustomerOption, PosDraftPayment, PosFiscalMode, PosPaymentMethodRow, PosStockWarningDetail } from '../domain/posTypes';
import { createEmptyFiscalBuyerDraft, type PosFiscalBuyerDraft } from '../../fiscal/domain-api';
import type { PosTipIntent } from '../application/posCheckoutTip';
import type { PickedAttachmentFile } from '../../shared/attachments/infra/pickAttachmentFile';

/** Resumen del estado de éxito in-modal (solo primer hito día 1). */
export type PosSaleCompleted = {
  total: number;
  received: number;
  change: number;
  methodLabel: string;
};

export type PosCheckoutState = {
  checkoutVisible: boolean;
  checkoutLoading: boolean;
  /** Venta registrada y modal sigue abierto celebrando el primer hito. */
  saleCompleted: PosSaleCompleted | null;
  paymentDrafts: PosDraftPayment[];
  saleFiscalMode: PosFiscalMode;
  /** El cajero eligió el modo a mano en este cobro — el auto 'auto_by_payment' ya no lo pisa. */
  saleFiscalModeTouched: boolean;
  fiscalBuyerDraft: PosFiscalBuyerDraft;
  paymentMethods: PosPaymentMethodRow[];
  customerOptions: PosCheckoutCustomerOption[];
  customersTotal: number;
  customerPageIndex: number;
  customerPageSize: number;
  saleCustomerId: string | null;
  pickupName: string;
  /** Propina salón (mesa / comer acá). Null = sin tip UI / sin propina. */
  tipIntent: PosTipIntent | null;
  /** Mozo asignado de la mesa → primary_staff al cobrar. */
  primaryStaffUserId: string | null;
  tipUiEnabled: boolean;
  /** Comprobantes elegidos en checkout — se suben post-venta anclados a pos.sales. */
  pendingAttachments: PickedAttachmentFile[];
  setCheckoutVisible: (v: boolean) => void;
  setCheckoutLoading: (v: boolean) => void;
  setPaymentDrafts: (updater: PosDraftPayment[] | ((c: PosDraftPayment[]) => PosDraftPayment[])) => void;
  setSaleFiscalMode: (m: PosFiscalMode) => void;
  setSaleFiscalModeTouched: (v: boolean) => void;
  setFiscalBuyerDraft: (patch: Partial<PosFiscalBuyerDraft> | PosFiscalBuyerDraft) => void;
  setPaymentMethods: (m: PosPaymentMethodRow[]) => void;
  setCustomerOptions: (c: PosCheckoutCustomerOption[]) => void;
  setCustomersTotal: (total: number) => void;
  setCustomerPageIndex: (index: number) => void;
  setSaleCustomerId: (id: string | null) => void;
  setPickupName: (name: string) => void;
  setTipIntent: (tip: PosTipIntent | null) => void;
  setPrimaryStaffUserId: (id: string | null) => void;
  setTipUiEnabled: (v: boolean) => void;
  addPendingAttachment: (file: PickedAttachmentFile) => void;
  removePendingAttachment: (index: number) => void;
  resetCheckout: () => void;
  /** Cierra el modal sin borrar saleCustomerId (preserva el cliente elegido). */
  dismissCheckout: () => void;
  /** Primer hito día 1: resetea el form pero deja el modal abierto en éxito. */
  showSaleCompleted: (summary: PosSaleCompleted) => void;

  stockWarnings: string[];
  stockWarningDetails: PosStockWarningDetail[];
  stockWarningsLoading: boolean;
  stockCanProceed: boolean;
  setStockWarningResult: (warnings: string[], details: PosStockWarningDetail[], canProceed: boolean) => void;
  setStockWarningsLoading: (v: boolean) => void;
  clearStockWarnings: () => void;

  showQrModal: boolean;
  lastMpQrSaleId: string | null;
  lastMpQrAmount: number | null;
  lastMpQrFiscalMode: PosFiscalMode | null;
  setShowQrModal: (v: boolean) => void;
  setLastMpQrSaleId: (id: string | null) => void;
  setLastMpQrAmount: (amount: number | null) => void;
  setLastMpQrFiscalMode: (mode: PosFiscalMode | null) => void;
  closeQrModal: () => void;
};

const initialCheckout = () => ({
  checkoutVisible: false,
  checkoutLoading: false,
  saleCompleted: null as PosSaleCompleted | null,
  paymentDrafts: [] as PosDraftPayment[],
  saleFiscalMode: 'non_fiscal' as PosFiscalMode,
  saleFiscalModeTouched: false,
  fiscalBuyerDraft: createEmptyFiscalBuyerDraft(),
  paymentMethods: [] as PosPaymentMethodRow[],
  customerOptions: [] as PosCheckoutCustomerOption[],
  customersTotal: 0,
  customerPageIndex: 0,
  customerPageSize: 5,
  saleCustomerId: null as string | null,
  pickupName: '',
  tipIntent: null as PosTipIntent | null,
  primaryStaffUserId: null as string | null,
  tipUiEnabled: false,
  pendingAttachments: [] as PickedAttachmentFile[],

  stockWarnings: [] as string[],
  stockWarningDetails: [] as PosStockWarningDetail[],
  stockWarningsLoading: false,
  stockCanProceed: true,

  showQrModal: false,
  lastMpQrSaleId: null as string | null,
  lastMpQrAmount: null as number | null,
  lastMpQrFiscalMode: null as PosFiscalMode | null,
});

export const usePosCheckoutStore = create<PosCheckoutState>((set) => ({
  ...initialCheckout(),

  setCheckoutVisible: (v) => set({ checkoutVisible: v }),
  setCheckoutLoading: (v) => set({ checkoutLoading: v }),
  setPaymentDrafts: (updater) =>
    set((state) => ({
      paymentDrafts: typeof updater === 'function' ? updater(state.paymentDrafts) : updater,
    })),
  setSaleFiscalMode: (m) =>
    set((state) => ({
      saleFiscalMode: m,
      fiscalBuyerDraft: m === 'fiscal' ? state.fiscalBuyerDraft : createEmptyFiscalBuyerDraft(),
    })),
  setSaleFiscalModeTouched: (v) => set({ saleFiscalModeTouched: v }),
  setFiscalBuyerDraft: (patch) =>
    set((state) => ({
      fiscalBuyerDraft: { ...state.fiscalBuyerDraft, ...patch },
    })),
  setPaymentMethods: (m) => set({ paymentMethods: m }),
  setCustomerOptions: (c) => set({ customerOptions: c }),
  setCustomersTotal: (total) => set({ customersTotal: total }),
  setCustomerPageIndex: (index) => set({ customerPageIndex: index }),
  setSaleCustomerId: (id) => set({ saleCustomerId: id }),
  setPickupName: (name) => set({ pickupName: name }),
  setTipIntent: (tip) => set({ tipIntent: tip }),
  setPrimaryStaffUserId: (id) => set({ primaryStaffUserId: id }),
  setTipUiEnabled: (v) => set({ tipUiEnabled: v }),
  addPendingAttachment: (file) =>
    set((state) => ({ pendingAttachments: [...state.pendingAttachments, file] })),
  removePendingAttachment: (index) =>
    set((state) => ({ pendingAttachments: state.pendingAttachments.filter((_, i) => i !== index) })),
  resetCheckout: () =>
    set({
      ...initialCheckout(),
    }),
  dismissCheckout: () =>
    set((state) => ({
      ...initialCheckout(),
      saleCustomerId: state.saleCustomerId,
    })),
  showSaleCompleted: (summary) =>
    set({
      ...initialCheckout(),
      checkoutVisible: true,
      saleCompleted: summary,
    }),

  setStockWarningResult: (warnings, details, canProceed) =>
    set({ stockWarnings: warnings, stockWarningDetails: details, stockCanProceed: canProceed }),
  setStockWarningsLoading: (v) => set({ stockWarningsLoading: v }),
  clearStockWarnings: () =>
    set({ stockWarnings: [], stockWarningDetails: [], stockWarningsLoading: false, stockCanProceed: true }),

  setShowQrModal: (v) => set({ showQrModal: v }),
  setLastMpQrSaleId: (id) => set({ lastMpQrSaleId: id }),
  setLastMpQrAmount: (amount) => set({ lastMpQrAmount: amount }),
  setLastMpQrFiscalMode: (mode) => set({ lastMpQrFiscalMode: mode }),
  closeQrModal: () =>
    set({
      showQrModal: false,
      lastMpQrSaleId: null,
      lastMpQrAmount: null,
      lastMpQrFiscalMode: null,
    }),
}));
